import { TPAComponentProps } from '../../types';
import type { ImageProps as WixImageProps } from '@wix/image';

export const ImageAspectRatioPresets = {
  square: 1,
  cinema: 16 / 9,
  landscape: 4 / 3,
};

export interface ImageFocalPoint {
  x: number;
  y: number;
}

export const ImageFocalPointPresets: { [key: string]: ImageFocalPoint } = {
  topLeft: { x: 0, y: 0 },
  topCenter: { x: 50, y: 0 },
  topRight: { x: 100, y: 0 },
  centerLeft: { x: 0, y: 50 },
  center: { x: 50, y: 50 },
  centerRight: { x: 100, y: 50 },
  bottomLeft: { x: 0, y: 100 },
  bottomCenter: { x: 50, y: 100 },
  bottomRight: { x: 100, y: 100 },
};
export interface ImageProps
  extends TPAComponentProps,
    Omit<
      WixImageProps,
      | 'width'
      | 'height'
      | 'alt'
      | 'uri'
      | 'name'
      | 'displayMode'
      | 'placeholderTransition'
    > {
  /** The source could be any absolute full URL or a relative URI of a media platform item */
  src?: string;
  /** The intrinsic width of the image in pixels */
  width?: number;
  /** The intrinsic height of the image in pixels */
  height?: number;
  /** The intrinsic width of the image itself in pixels */
  sourceWidth?: number;
  /** The intrinsic height of the image itself in pixels */
  sourceHeight?: number;
  /** The alternative text description of the image. Allowing better SEO when the image has meaning of content */
  alt?: string;
  /** The error message that is shown when we failed to load the image */
  errorMessage?: string;
  /** Specifies how the image is resized to fit its container */
  resize?: ImageResizeOptions;
  /** Specifies the proportional relationship between width and height */
  aspectRatio?: keyof typeof ImageAspectRatioPresets | number;
  /** Centers a point of the image by given coordinates of x and y in percentages */
  focalPoint?:
    | Extract<keyof typeof ImageFocalPointPresets, 'string'>
    | ImageFocalPoint;
  /** Specifies wether the image adapts and scales relatively to the containing layout */
  fluid?: boolean;
  /** An effect to appear when the image is hovered  */
  hoverEffect?: ImageHoverEffectOptions;
  /** An experience to set while the image is fetched and loaded  */
  loadingBehavior?: ImageLoadingBehaviorOptions;
  /** Controls whether image smaller than the container should be upscaled */
  stretchImage?: boolean;
  /** EXPERIMENTAL. Forces the contain of the image element. Requires `contianerAspectRatio` to be passed. */
  forceImageContain?: boolean;
  /** Container aspect ratio for `forceImageContain` to properly work. */
  containerAspectRatio?: number;
}

export interface ImageDimensions {
  width: ImageProps['width'];
  height: ImageProps['height'];
}

export enum ImageResizeOptions {
  contain = 'contain',
  cover = 'cover',
}

export enum ImageHoverEffectOptions {
  none = 'none',
  zoom = 'zoom',
  darken = 'darken',
}

export enum ImageLoadingBehaviorOptions {
  none = 'none',
  blur = 'blur',
  spinner = 'spinner',
}
